// Shared presentational helpers for the CRM Executive Dashboard sections.
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

export const CHART_COLORS = ["#1F4E2C", "#F39C12", "#0EA5E9", "#94A3B8", "#DC2626", "#7C3AED", "#15803D", "#B45309"];

// One distinct accent per section — gives the dashboard visual variety instead of
// a single flat green everywhere, while staying inside the CRM's dark executive palette.
export const SECTION_ACCENTS = {
  sc: { badge: "bg-[#B45309]", text: "text-[#B45309]", ring: "#F59E0B", chart: ["#B45309", "#F59E0B", "#FCD34D", "#94A3B8", "#DC2626"], border: "border-t-amber-500" },
  opportunity: { badge: "bg-[#0369A1]", text: "text-[#0369A1]", ring: "#0EA5E9", chart: ["#0369A1", "#0EA5E9", "#7DD3FC", "#94A3B8", "#DC2626"], border: "border-t-sky-500" },
  quotation: { badge: "bg-[#6D28D9]", text: "text-[#6D28D9]", ring: "#8B5CF6", chart: ["#6D28D9", "#8B5CF6", "#C4B5FD", "#94A3B8", "#DC2626"], border: "border-t-violet-500" },
  ticket: { badge: "bg-[#B91C1C]", text: "text-[#B91C1C]", ring: "#EF4444", chart: ["#0EA5E9", "#F39C12", "#94A3B8", "#B91C1C", "#1F4E2C", "#15803D"], border: "border-t-red-500" },
  customerReferral: { badge: "bg-[#0F766E]", text: "text-[#0F766E]", ring: "#14B8A6", chart: ["#0F766E", "#14B8A6", "#5EEAD4", "#94A3B8", "#DC2626"], border: "border-t-teal-500" },
  customerApp: { badge: "bg-[#4338CA]", text: "text-[#4338CA]", ring: "#6366F1", chart: ["#4338CA", "#6366F1", "#A5B4FC", "#94A3B8"], border: "border-t-indigo-500" },
  installedBase: { badge: "bg-[#1F4E2C]", text: "text-[#1F4E2C]", ring: "#22C55E", chart: ["#1F4E2C", "#22C55E", "#86EFAC", "#94A3B8"], border: "border-t-emerald-500" },
};

export const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export function monthKey(iso) {
  if (!iso) return null;
  return String(iso).slice(0, 7);
}

export function monthLabel(key) {
  if (!key) return "—";
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
}

export function last6Months(byMonth) {
  const months = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return months.map((m) => ({ month: monthLabel(m), value: byMonth[m] || 0 }));
}

/** Wraps a whole insight section: coloured top border, fade-up entrance, hover lift. */
export function SectionCard({ accent, delay = 0, children, testid }) {
  const a = SECTION_ACCENTS[accent] || SECTION_ACCENTS.installedBase;
  return (
    <div
      data-testid={testid}
      className={"bg-white border border-[#E2E4DC] border-t-[3px] " + a.border + " p-4 animate-in fade-in slide-in-from-bottom-2 fill-mode-both hover:shadow-[0_6px_20px_rgba(15,23,42,0.08)] transition-shadow duration-300"}
      style={{ animationDelay: `${delay}ms`, animationDuration: "500ms" }}
    >
      {children}
    </div>
  );
}

export function SectionHeader({ icon: Icon, title, subtitle, accent }) {
  const a = SECTION_ACCENTS[accent] || SECTION_ACCENTS.installedBase;
  return (
    <div className="flex items-center gap-2.5 mb-3">
      <div className={"w-8 h-8 flex items-center justify-center flex-shrink-0 " + a.badge}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div>
        <div className="font-display text-xl font-bold leading-none text-[#0A1128]">{title}</div>
        {subtitle && <div className="text-[11.5px] text-[#4A5568] mt-0.5">{subtitle}</div>}
      </div>
    </div>
  );
}

/** KPI chip — pass `to` to make it a drill-down link straight to the report page. */
export function KpiChip({ label, value, sub, tone = "bg-slate-100 text-slate-800", to, testid }) {
  const navigate = useNavigate();
  const clickable = !!to;
  return (
    <div
      onClick={clickable ? () => navigate(to) : undefined}
      data-testid={testid}
      className={"px-2.5 py-1.5 flex items-center gap-1.5 " + tone + (clickable ? " cursor-pointer hover:scale-[1.04] hover:shadow-md active:scale-[0.98] transition-transform duration-150" : "")}
      title={clickable ? "Click to open the full report" : undefined}
    >
      <div className="font-display text-[16px] font-bold leading-none tabular-nums">{value}{sub !== undefined && <span className="text-[11px] font-mono opacity-60 ml-0.5">/{sub}</span>}</div>
      <div className="font-mono text-[9px] uppercase tracking-widest leading-none">{label}</div>
    </div>
  );
}

export function ChartCard({ title, children, testid }) {
  return (
    <div className="border border-[#E2E4DC] bg-white p-3" data-testid={testid}>
      <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#4A5568] mb-2">{title}</div>
      <div style={{ width: "100%", height: 180 }}>{children}</div>
    </div>
  );
}

/** Pass `to` to navigate to the report page on slice click. */
export function MiniPie({ data, formatter, colors, to }) {
  const navigate = useNavigate();
  const palette = colors || CHART_COLORS;
  return (
    <ResponsiveContainer>
      <PieChart>
        <Pie
          data={data} dataKey="value" nameKey="name" innerRadius={34} outerRadius={54} paddingAngle={2}
          cursor={to ? "pointer" : "default"}
          onClick={to ? () => navigate(to) : undefined}
        >
          {data.map((_, i) => <Cell key={i} fill={palette[i % palette.length]} />)}
        </Pie>
        <Tooltip contentStyle={{ fontSize: 11, fontFamily: "monospace" }} formatter={formatter} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function MiniBar({ data, dataKey = "value", color = "#1F4E2C", formatter, xKey = "month", to }) {
  const navigate = useNavigate();
  return (
    <ResponsiveContainer>
      <BarChart data={data} margin={{ left: -20, right: 8, top: 4, bottom: 0 }}>
        <CartesianGrid strokeDasharray="2 2" stroke="#EEF2EA" vertical={false} />
        <XAxis dataKey={xKey} tick={{ fontSize: 9, fontFamily: "monospace" }} interval={0} angle={xKey !== "month" ? -20 : 0} textAnchor={xKey !== "month" ? "end" : "middle"} height={xKey !== "month" ? 40 : 24} />
        <YAxis tick={{ fontSize: 9, fontFamily: "monospace" }} allowDecimals={false} />
        <Tooltip contentStyle={{ fontSize: 11, fontFamily: "monospace" }} formatter={formatter} />
        <Bar dataKey={dataKey} fill={color} barSize={18} cursor={to ? "pointer" : "default"} onClick={to ? () => navigate(to) : undefined} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function StackedBar({ data, bars, xKey = "name", to }) {
  const navigate = useNavigate();
  return (
    <ResponsiveContainer>
      <BarChart data={data} margin={{ left: -20, right: 8, top: 4, bottom: 0 }}>
        <CartesianGrid strokeDasharray="2 2" stroke="#EEF2EA" vertical={false} />
        <XAxis dataKey={xKey} tick={{ fontSize: 8.5, fontFamily: "monospace" }} interval={0} />
        <YAxis tick={{ fontSize: 9, fontFamily: "monospace" }} allowDecimals={false} />
        <Tooltip contentStyle={{ fontSize: 11, fontFamily: "monospace" }} />
        <Legend wrapperStyle={{ fontSize: 9, fontFamily: "monospace" }} />
        {bars.map((b, i) => (
          <Bar key={b.key} dataKey={b.key} stackId="s" fill={b.color || CHART_COLORS[i % CHART_COLORS.length]} name={b.label || b.key}
            cursor={to ? "pointer" : "default"} onClick={to ? () => navigate(to) : undefined} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export function pctOf(num, den) {
  return den > 0 ? Math.round((num / den) * 100) : 0;
}
